/**
 * Compact branded QR callout geometry for letter HTML preview and PDF renderers.
 * 90pt white square + quiet zone. Real QR bytes are supplied by Lot 6.9C.1
 * only when rendering an actual campaign mailing — this module stays token-free.
 */

export const LETTER_TEMPLATE_QR_PLACEHOLDER_SIZE_PT = 90;
export const LETTER_TEMPLATE_QR_LEGACY_PLACEHOLDER_SIZE_PT = 120;
export const LETTER_TEMPLATE_QR_LEGACY_FLOW_AFTER_PT = 8;

/** Inset inside the 90pt white square reserved for a future 4-module quiet zone. */
export const LETTER_TEMPLATE_QR_QUIET_ZONE_PT = 10;

export const LETTER_TEMPLATE_QR_CALLOUT_WIDTH_PT = 360;
export const LETTER_TEMPLATE_QR_CALLOUT_MAX_HEIGHT_PT = 108;
export const LETTER_TEMPLATE_QR_CALLOUT_PADDING_PT = 5;
export const LETTER_TEMPLATE_QR_CALLOUT_GAP_PT = 12;
export const LETTER_TEMPLATE_QR_CALLOUT_RADIUS_PT = 8;
export const LETTER_TEMPLATE_QR_CALLOUT_BORDER_WIDTH_PT = 1;
export const LETTER_TEMPLATE_QR_CALLOUT_SPACING_BEFORE_PT = 4;
export const LETTER_TEMPLATE_QR_CALLOUT_SPACING_AFTER_PT = 4;

export const LETTER_TEMPLATE_QR_CALLOUT_HEIGHT_PT =
  LETTER_TEMPLATE_QR_CALLOUT_PADDING_PT * 2 + LETTER_TEMPLATE_QR_PLACEHOLDER_SIZE_PT;

export const LETTER_TEMPLATE_QR_CALLOUT_BORDER_COLOR = "#003f52";
export const LETTER_TEMPLATE_QR_CALLOUT_BACKGROUND_COLOR = "#eef6f7";
export const LETTER_TEMPLATE_QR_CALLOUT_TITLE_COLOR = "#003f52";
export const LETTER_TEMPLATE_QR_CALLOUT_SUBTITLE_COLOR = "#1f4a52";
export const LETTER_TEMPLATE_QR_CALLOUT_QR_BACKGROUND_COLOR = "#ffffff";

export const LETTER_TEMPLATE_QR_CALLOUT_TITLE_FR = "Découvrez la valeur de votre immeuble";
export const LETTER_TEMPLATE_QR_CALLOUT_SUBTITLE_FR = "Gratuit et sans engagement";
export const LETTER_TEMPLATE_QR_CALLOUT_TITLE_EN = "Discover your property’s value";
export const LETTER_TEMPLATE_QR_CALLOUT_SUBTITLE_EN = "Free and without obligation";

export const LETTER_TEMPLATE_QR_PLACEHOLDER_LABEL = "QR";

const PAGE_WIDTH_PT = 612;
const PAGE_HEIGHT_PT = 792;
const PAGE_MARGIN_PT = 54;
const PRINTABLE_WIDTH_PT = PAGE_WIDTH_PT - PAGE_MARGIN_PT * 2;
const PRINTABLE_BOTTOM_Y = PAGE_HEIGHT_PT - PAGE_MARGIN_PT;

const OVERFLOW_REASON = "letter_template_overflow";

/**
 * @param {unknown} align
 * @returns {'left' | 'center' | 'right'}
 */
export function resolveLetterTemplateQrCalloutAlign(align) {
  if (align === "left" || align === "right" || align === "center") return align;
  return "center";
}

/**
 * Pure QR callout layout: width, height, QR/CTA positions, alignment, flowY, overflow.
 *
 * @param {{
 *   startY?: number,
 *   align?: string | null,
 *   bilingual?: boolean,
 *   printableWidth?: number,
 *   printableLeft?: number,
 *   printableBottomY?: number,
 *   spacingBefore?: number,
 *   spacingAfter?: number,
 * }} [input]
 */
export function computeLetterTemplateQrCalloutLayout({
  startY = 0,
  align = "center",
  bilingual = false,
  printableWidth = PRINTABLE_WIDTH_PT,
  printableLeft = PAGE_MARGIN_PT,
  printableBottomY = PRINTABLE_BOTTOM_Y,
  spacingBefore = LETTER_TEMPLATE_QR_CALLOUT_SPACING_BEFORE_PT,
  spacingAfter = LETTER_TEMPLATE_QR_CALLOUT_SPACING_AFTER_PT,
} = {}) {
  const resolvedAlign = resolveLetterTemplateQrCalloutAlign(align);
  const width = LETTER_TEMPLATE_QR_CALLOUT_WIDTH_PT;
  const height = LETTER_TEMPLATE_QR_CALLOUT_HEIGHT_PT;
  const originY = Number(startY);
  const y = originY + spacingBefore;
  let x = printableLeft;
  if (resolvedAlign === "center") {
    x = printableLeft + (printableWidth - width) / 2;
  } else if (resolvedAlign === "right") {
    x = printableLeft + printableWidth - width;
  }

  const pad = LETTER_TEMPLATE_QR_CALLOUT_PADDING_PT;
  const qrSize = LETTER_TEMPLATE_QR_PLACEHOLDER_SIZE_PT;
  const quiet = LETTER_TEMPLATE_QR_QUIET_ZONE_PT;
  const qrX = x + pad;
  const qrY = y + pad;
  const innerSize = Math.max(0, qrSize - quiet * 2);
  const ctaX = qrX + qrSize + LETTER_TEMPLATE_QR_CALLOUT_GAP_PT;
  const ctaWidth = x + width - pad - ctaX;
  const endY = y + height;
  const flowY = endY + spacingAfter;
  const overflow = !Number.isFinite(originY) || flowY > printableBottomY + 0.5;

  return {
    ok: !overflow,
    reason: overflow ? OVERFLOW_REASON : undefined,
    width,
    height,
    x,
    y,
    endY,
    flowY,
    align: resolvedAlign,
    bilingual: Boolean(bilingual),
    qr: {
      x: qrX,
      y: qrY,
      size: qrSize,
      innerX: qrX + quiet,
      innerY: qrY + quiet,
      innerSize,
    },
    cta: {
      x: ctaX,
      y: qrY,
      width: ctaWidth,
      height: qrSize,
    },
  };
}

/**
 * Vertical space occupied by the former 120pt centered QR square (+ trailing gap).
 */
export function letterTemplateLegacyQrFlowHeightPt() {
  return LETTER_TEMPLATE_QR_LEGACY_PLACEHOLDER_SIZE_PT + LETTER_TEMPLATE_QR_LEGACY_FLOW_AFTER_PT;
}

/**
 * Vertical space occupied by the compact callout, including before/after spacing.
 */
export function letterTemplateQrCalloutFlowHeightPt() {
  return (
    LETTER_TEMPLATE_QR_CALLOUT_SPACING_BEFORE_PT +
    LETTER_TEMPLATE_QR_CALLOUT_HEIGHT_PT +
    LETTER_TEMPLATE_QR_CALLOUT_SPACING_AFTER_PT
  );
}
